import { Component, OnInit, OnDestroy, Directive, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { TextTools } from 'appshared/text-tools';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';
import { SharedLicenseTools } from 'appshared/shared-license-tools';
import { LicenseValidationTools } from 'appshared/license-validation-tools';

import { AuthService } from 'client/app/services/auth.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';
import { PlatformsService } from 'client/app/components/app-sections/platforms/platforms.service';

import { PagingSearchBarComponent } from 'client/app/components/shared/paging-search-bar/paging-search-bar.component';

@Component({
	selector: 'app-table-billing-codes',
	templateUrl: './table-billing-codes.component.html',
	styleUrls: []
})
export class TableBillingCodesComponent {
	ac = AppConstants;
	tt = TextTools;
	mt = MiscTools;
	slt = SharedLicenseTools;

	now = new Date();

	@ViewChild(PagingSearchBarComponent) pagingSearchBar: PagingSearchBarComponent = null;

	@Input() parentApi !: any;
	@Input() showAuthColumn: boolean = false;
	@Input() showAuthCode: boolean = false;
	@Input() canManage: boolean = false;
	@Input() showDisable: boolean = false;
	@Input() loading: boolean = false;

	authUser: Models.AuthUser = null;

	displayOptions: Models.TableDisplayOptions = new Models.TableDisplayOptions();
	pagingOptions: Models.TablePagingOptions = new Models.TablePagingOptions();
	storageKey: string = '';

	items: Models.BillingCode[] = [];
	itemsToShow: Models.BillingCode[] = [];

	columnDefs: Models.ColumnDefinition[] = [
		{
			field: 'label',
			type: 'baseText',
			sortType: 'text',
			label: 'Name/Label',
			toolTip: null,
			icon: null
		}, {
			field: 'billing_code',
			type: 'baseText',
			sortType: 'text',
			label: 'Code',
			toolTip: null,
			icon: null
		}, {
			field: 'auth_code',
			type: 'baseText',
			sortType: 'text',
			label: 'Authorization',
			toolTip: null,
			icon: null
		}, {
			field: 'added_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: 'Added',
			toolTip: null,
			icon: null
		}, {
			field: 'edited_on',
			type: 'baseDate',
			sortType: 'numeric',
			label: 'Edited',
			toolTip: null,
			icon: null
		}
	];

	fieldsToShow: string[] = [];
	allFieldsToShow: string[] = [];
	lockedFields: string[] = [];
	baselineFields: string[] = [];
	minimalFields: string[] = [];

	// ------------------------------------------------------------------------
	constructor(
		private router: Router,
		private platformsService: PlatformsService,
		private uiAlertsService: UiAlertsService,
		private authService: AuthService) {
	}

	// ------------------------------------------------------------------------
	ngOnInit(): void {
		const sessionUser: Models.AuthUser = this.authService.getUser();
		if (sessionUser != null && sessionUser.id !== 0) {
			this.authUser = sessionUser;
		} // if
	}
	// ------------------------------------------------------------------------
	updateContent(items: Models.BillingCode[], storageKey: string, options: any = {}) {
		this.items = items;
		this.storageKey = storageKey;

		let optionKeys: string[] = [];
		if (options) optionKeys = Object.keys(options);

		this.fieldsToShow = ['label', 'billing_code', 'added_on', 'edited_on'];
		if (this.showAuthColumn) this.fieldsToShow.push('auth_code');

		for (const item of this.items) {

		} // for

		this.pagingSearchBar.setupPaging(this.items, this.columnDefs, this.storageKey, 'added_on', 'asc');
	} // updateContent

	// ------------------------------------------------------------------------
	getPlatformIDsFromPlatformFiles(platformFiles: Models.PlatformFile[]): number[] {
		const ids = [];
		for (const pf of platformFiles)
			ids.push(pf.platform_id);
		return ids;
	}

	// ------------------------------------------------------------------------
	filterItems(matchWords: string[]): any[] {
		const tmpList: Models.BillingCode[] = [];
		for (const item of this.items) {
			let match: boolean = true;
			if (matchWords.length > 0) {
				let matches: number = 0;
				for (const w of matchWords) {
					if (item.label.toLowerCase().includes(w)
						|| item.billing_code.toLowerCase().includes(w)
					)
						matches++;
				} // for
				if (matches !== matchWords.length) match = false;
			} // if

			if (match) tmpList.push(item);
		} // for
		return tmpList;
	}

	// ------------------------------------------------------------------------
	setSortBy(field: string) {
		this.pagingSearchBar.setSortBy(field);
	} // setSortBy

	// *********************************************************
	async resetBillCodeAuthCodePrep(billingCode: Models.BillingCode) {
		if (this.parentApi) this.parentApi.resetBillCodeAuthCodePrep(billingCode);
	}

	// *********************************************************
	async updateBillingCodePrep(billingCode: Models.BillingCode) {
		if (this.parentApi) this.parentApi.updateBillingCodePrep(billingCode);
	}

	// *********************************************************
	async disableBillingCode(billingCode: Models.BillingCode) {
		if (this.parentApi) this.parentApi.disableBillingCode(billingCode);
	}

	// *********************************************************
	async enableBillingCode(billingCode: Models.BillingCode) {
		if (this.parentApi) this.parentApi.enableBillingCode(billingCode);
	}

	// ------------------------------------------------------------------------
	// Catch calls from the paging/search bar
	// ------------------------------------------------------------------------
	getParentMethod(): any {
		return {
			filterItems: (matchWords: string[]) => {
				return this.filterItems(matchWords);
			},
			updateItemsToShow: (itemsToShow: any[], displayOptions: Models.TableDisplayOptions, pagingOptions: Models.TablePagingOptions) => {
				this.itemsToShow = itemsToShow;
				this.displayOptions = displayOptions;
				this.pagingOptions = pagingOptions;
			},
			updateFieldsToShow: (fieldsToShow: string[]) => {
				this.fieldsToShow = fieldsToShow;
			},
			setPageNum: (pageNum: number, uniqueId: string) => {
				if (this.pagingSearchBar) this.pagingSearchBar.setPageNum(pageNum);
			},
			setSortBy: (field: string) => {
				this.setSortBy(field);
			}
		}
	}

	// --------------------------------------------------------------------
	copyToClipboardAlert(item: string = '') {
		this.uiAlertsService.copyToClipboardAlert(item);
	}

}
